import React from "react";
import { MultimediaCategory } from "../../../types";
import { Button } from "../../UI/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { Col, Container, Row } from "../../common/Grid";
import { Input, Label, Radio } from "../../UI/Forms/FormElements";
import { FormControl } from "@mui/material";
import { languages } from "../../../config/config";
import Feedback from "../../UI/Forms/FormElements/Feedback";

type Props = {
  category?: MultimediaCategory;
  isSubmitting: boolean;
  defaultOrderNumber: number | null;
  onSubmit: (values: MultimediaCategory) => void;
  onCancel: () => void;
};

const MultimediaCategoryForm: React.FC<Props> = ({
  category,
  isSubmitting,
  defaultOrderNumber,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation();

  const createLabelsObject = (array: any) => {
    let labelsObject = array.reduce((acc: any, obj: any) => {
      return {
        ...acc,
        [obj.lang]: category?.labels[obj.lang] ? category.labels[obj.lang] : "",
      };
    }, {});
    return labelsObject;
  };

  const createLabelsYupObject = (array: any) => {
    let labelsObject = array.reduce((acc: any, obj: any) => {
      return {
        ...acc,
        [obj.lang]: Yup.string().required(
          t("components.categoryForm.langRequired")
        ),
      };
    }, {});
    return labelsObject;
  };

  const formik = useFormik({
    initialValues: {
      id: category?.id ? category?.id : null,
      name: category?.name || "",
      labels: createLabelsObject(languages),
      visibleInApp: category?.visibleInApp === true ? "visible" : "notVisible",
      orderNumber: category?.orderNumber || defaultOrderNumber || 1,
      multimediaOrder: category?.multimediaOrder || "asc",
    },
    enableReinitialize: true,
    onSubmit: onSubmit,
    validationSchema: Yup.object().shape({
      name: Yup.string().required(t("components.categoryForm.nameRequired")),
      labels: Yup.object().shape(createLabelsYupObject(languages)),
      visibleInApp: Yup.string().required(
        t("components.categoryForm.visibleInAppRequired")
      ),
      orderNumber: Yup.number().min(
        1,
        t("components.categoryForm.orderNumberRequired")
      ),
    }),
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Container>
        <Row mt="10px">
          <Col noGutter>
            <FormControl fullWidth>
              <Label htmlFor="orderNumber">
                {t("components.categoryForm.orderNumber")}
              </Label>
              <Input
                type="number"
                id="orderNumber"
                name="orderNumber"
                placeholder={t(
                  "components.categoryForm.orderNumberPlaceholder"
                )}
                value={formik.values.orderNumber}
                onChange={formik.handleChange}
                state={formik.errors.orderNumber ? "error" : "success"}
                showState={true}
                feedbackText={formik.getFieldMeta("orderNumber").error}
                disabled
              />
            </FormControl>
          </Col>
        </Row>

        <Row>
          <Col noGutter>
            <FormControl fullWidth>
              <Label htmlFor="as">{t("components.categoryForm.name")}</Label>
              <Input
                id=""
                name="name"
                placeholder={t("components.categoryForm.namePlaceholder")}
                value={formik.values.name}
                onChange={formik.handleChange}
                state={formik.errors.name ? "error" : "success"}
                showState={true}
                feedbackText={formik.getFieldMeta("name").error}
              />
            </FormControl>
          </Col>
        </Row>

        {languages.map((language: any) => {
          const lang = language.lang;
          return (
            <Row paddingTop="10px" key={language.lang}>
              <Col noGutter>
                <FormControl fullWidth>
                  <Label htmlFor="as">{language.description}</Label>
                  <Input
                    id={`labels.${lang}`}
                    name={`labels.${lang}`}
                    placeholder={t("components.categoryForm.namePlaceholder")}
                    value={formik.values.labels[lang] as unknown as string}
                    onChange={formik.handleChange}
                    state={formik.errors.labels?.[lang] ? "error" : "success"}
                    // state={"error"}
                    showState={true}
                    feedbackText={formik.getFieldMeta(`labels.${lang}`).error}
                  />
                </FormControl>
              </Col>
            </Row>
          );
        })}
        <Row paddingTop="10px">
          <Col noGutter>
            <FormControl fullWidth>
              <Label htmlFor="as">{t("components.categoryForm.state")}</Label>
              <Radio
                id="visible1"
                name="visibleInApp"
                value={"visible"}
                label={t("components.categoryForm.visibleInApp")}
                onChange={formik.handleChange}
                checked={formik.values.visibleInApp === "visible"}
              />
              <Radio
                id="visible2"
                name="visibleInApp"
                value={"notVisible"}
                label={t("components.categoryForm.notVisibleInApp")}
                onChange={formik.handleChange}
                checked={formik.values.visibleInApp === "notVisible"}
              />

              {formik.errors.visibleInApp && (
                <Feedback state="error" showState={true} showErrorOnly={true}>
                  {formik.getFieldMeta("visibleInApp").error}
                </Feedback>
              )}
            </FormControl>
          </Col>
        </Row>
        <Row paddingTop="10px">
          <Col noGutter>
            <FormControl fullWidth>
              <Label htmlFor="as">
                {t("components.categoryForm.multimediaOrder")}
              </Label>
              <Radio
                id="multimediaOrder1"
                name="multimediaOrder"
                value={"asc"}
                label={t("components.categoryForm.olderMultimediaFirst")}
                onChange={formik.handleChange}
                checked={formik.values.multimediaOrder === "asc"}
              />
              <Radio
                id="multimediaOrder2"
                name="multimediaOrder"
                value={"desc"}
                label={t("components.categoryForm.newestMultimediaFirst")}
                onChange={formik.handleChange}
                checked={formik.values.multimediaOrder === "desc"}
              />

              {formik.errors.multimediaOrder && (
                <Feedback state="error" showState={true} showErrorOnly={true}>
                  {formik.getFieldMeta("multimediaOrder").error}
                </Feedback>
              )}
            </FormControl>
          </Col>
        </Row>
      </Container>

      <Row>
        <Col textAlign="end">
          <Button
            type={"reset"}
            onClick={onCancel}
            marginRight="10px"
            color="light"
            disabled={isSubmitting}
          >
            {t("common.cancel")}
          </Button>
          <Button type={"submit"}>{t("common.save")}</Button>
        </Col>
      </Row>
    </form>
  );
};

export default MultimediaCategoryForm;
